'use client';

import { useContext } from 'react';

import { useEvent } from 'react-use';

import { useConfigValue, useTenantName } from '@/components/providers/config-provider';
import { ABORT_REASONS, CHAT_ANALYTICS_EVENTS } from '@/lib/constants/post-message-types';
import { markResponseInterrupted } from '@/lib/hitl/resumable-storage';
import { sendAnalyticsBeacon } from '@/lib/utils/beacon';

import {
  buildInterruptedEventPayload,
  cleanupPendingResponse,
  getHadStreamingStarted,
  getIsRetry,
} from './analytics-utils';
import { ResponseTrackingContext } from './use-response-tracking';

export function useResponseInterruption(): void {
  const context = useContext(ResponseTrackingContext);
  const resumableEnabled = useConfigValue<boolean>('resumableStreaming') === true;
  const tenantName = useTenantName();

  useEvent('pagehide', (event: PageTransitionEvent) => {
    const pendingResponses = context?.pendingResponsesRef.current;
    if (!pendingResponses?.size) return;

    pendingResponses.forEach((startTime, threadId) => {
      sendAnalyticsBeacon({
        event: CHAT_ANALYTICS_EVENTS.RESPONSE_INTERRUPTED,
        properties: buildInterruptedEventPayload({
          threadId,
          startTime,
          hadStreamingStarted: getHadStreamingStarted(context, threadId),
          abortReason: ABORT_REASONS.PAGE_NAVIGATION,
          isRetry: getIsRetry(context, threadId),
          isPagePersisted: event.persisted,
        }),
      });
      // Resumable tenants only: stamp the interruption so a resume after reload can report
      // interrupt→resume latency. Gated so non-resumable tenants stay byte-identical.
      if (resumableEnabled) markResponseInterrupted({ tenantName, threadUuid: threadId });
      cleanupPendingResponse(context, threadId);
    });
  });
}
